import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

import VariantsTableDesktop from './VariantsTable/VariantsTableDesktop';

import Checkbox from '../../common/Checkbox';
import TagsInput from '../../common/TagsInput';
import Select from '../../common/Select';
import Button from '../../common/Button';

import { fetchUpdateProductAppearance } from '../../../actions/items';
import { showModal } from '../../../actions/modal';

import { getCurrentFeature, parseError } from '../../../helpers';
import isMobile from 'ismobilejs';
import VariantsTableMobile from './VariantsTable/VariantsTableMobile';
import Adaptive from '../../common/Adaptive';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const isNumeric = (n) => {
  return !isNaN(parseFloat(n)) && isFinite(n);
};

const getTagsInputValue = (options, i, features) => {
  const currentFeature = getCurrentFeature(options, i, features);
  if (currentFeature) {
    if (currentFeature.type === 'string') {
      return options[i].values.map((v) => v.stringValue);
    }
    if (currentFeature.type === 'decimal') {
      return options[i].values.map((v) => v.decimalValue);
    }
  }
  return [];
};

const getTagsInputAppearance = (options, i, features, appearanceFields) => {
  const currentFeature = getCurrentFeature(options, i, features);
  if (currentFeature) {
    const filteredAppearance = appearanceFields.filter((a) => a.feature === currentFeature.code);
    if (filteredAppearance.length) {
      return filteredAppearance;
    }
  }
  return null;
};

const getTagsInputValidation = (value, options, i, features) => {
  const currentFeature = getCurrentFeature(options, i, features);
  if (currentFeature) {
    if (value === '') return true;
    if (currentFeature.type === 'decimal') {
      return isNumeric(value);
    }
    if (currentFeature.type === 'string') {
      return true;
    }
  }
  return false;
};

const VariantsBlock = (props) => {
  const {
    productId,
    loading,
    isSavedVariants,
    needSaving,
    features,
    options,
    variants,
    variantsValidation,
    selectedVariants,
    allSelectedVariants,
    appearanceFields,
    handleSelectVariant,
    handleAllSelectVariants,
    updateVariant,
    deleteVariant,
    deleteLoading,
    setOptions,
    useVariants,
    addNewFeature,
    handleVariantsUsing,
    handleGenerateVariants,
    handleNeedSaving,
    handleSetLastAction,
    handleAddManualVariant,
    setUnsavedStatus,
    openVariant,
    refreshArrearance,
    moveVariant,
  } = props;

  const dispatch = useDispatch();
  const [showOptions, setShowOptions] = useState(false);
  const [showNeedSaveAlert, setShowingNeedSaveStatus] = useState(false);

  const { t } = useAutoTranslation();

  useEffect(() => {
    if (!needSaving && showNeedSaveAlert) {
      setShowingNeedSaveStatus(false);
    }
  }, [needSaving]);

  const setFeatureAsOption = (data, i) => {
    let currentFeature = null;
    const isNewFeature = data.__isNew__;

    if (isNewFeature) {
      currentFeature = {
        id: uuidv4(),
        code: true,
        values: [],
        title: data.label,
        type: 'string',
        isNewFeature: true,
      };
      dispatch(
        showModal('EditOption', {
          option: currentFeature,
          submitCallback: async (newData) => {
            currentFeature = newData;
            await addNewFeature(currentFeature);
            updateOptions({ ...currentFeature, isNewFeature: false });
          },
        }),
      );
      return false;
    } else {
      const featureId = data.value;
      currentFeature = features.find((f) => f.code === featureId);
    }

    if (currentFeature) {
      updateOptions(currentFeature);
    }

    function updateOptions(currentFeature) {
      const newArray = options.slice();
      newArray[i] = {
        ...currentFeature,
        //values:currentFeature.values||[],
        values: [],
        isNew: true,
      };
      setOptions(newArray);
      if (currentFeature.values) setUnsavedStatus(true);
    }
  };

  const updateOptionValues = (optionValues, newValues, i) => {
    const newArray = options.slice();
    const updatedValue = newValues[0];
    const currentFeature = getCurrentFeature(newArray, i, features);
    let newData = null;

    if (!currentFeature) return false;

    if (needSaving) {
      setShowingNeedSaveStatus(true);
      return false;
    }

    if (optionValues.indexOf(updatedValue) !== -1) {
      // добавление тега
      if (currentFeature.type === 'string') {
        newData = {
          stringValue: updatedValue,
          decimalValue: null,
        };
      }
      if (currentFeature.type === 'decimal') {
        newData = {
          stringValue: null,
          decimalValue: updatedValue,
        };
      }
      if (newData) {
        newArray[i].values.push({ ...newData, isNew: true });
        if (isSavedVariants) {
          handleSetLastAction('addTag', {
            newOption: { ...newData, code: currentFeature.code, title: currentFeature.title },
          });
        }
      }
    } else {
      // удаление тега
      const index = newArray[i].values.findIndex((v) => {
        if (currentFeature.type === 'string') {
          return v.stringValue === updatedValue;
        }
        if (currentFeature.type === 'decimal') {
          return v.decimalValue === updatedValue;
        }
        return false;
      });
      if (index !== -1) {
        if (isSavedVariants) {
          handleSetLastAction('removeTag', {
            tag: { ...options[i].values[index], code: currentFeature.code },
          });
        }
        newArray[i].values.splice(index, 1);
      }
    }
    setOptions(newArray);
    setUnsavedStatus(true);
    handleGenerateVariants();
    if (isSavedVariants) {
      handleNeedSaving();
    }
  };

  const addOption = () => {
    const newArray = options.slice();
    newArray.push({});
    setOptions(newArray);
    if (!showOptions) setShowOptions(true);
  };

  const removeOption = (i) => {
    if (needSaving) {
      setShowingNeedSaveStatus(true);
      return false;
    }

    const removingOption = Object.assign({}, options[i]);
    const newArray = options.slice();
    newArray.splice(i, 1);
    setOptions(newArray);
    setUnsavedStatus(true);
    handleGenerateVariants();
    if (isSavedVariants && removingOption.values.length) {
      handleSetLastAction('removeOption', { option: removingOption });
      handleNeedSaving();
    }
  };

  const handleClickAppearance = ({ currentAppearance, tag }, option) => {
    dispatch(
      showModal('EditAppearance', {
        tag,
        currentAppearance,
        submitCallback: (newValues) => {
          return dispatch(
            fetchUpdateProductAppearance({
              productId,
              feature: option.code,
              value: tag,
              ...newValues,
            }),
          )
            .then(() => {
              toast.info(t('Представление успешно сохранено'));
              refreshArrearance();
            })
            .catch((err) => {
              toast.error(parseError(err));
            });
        },
      }),
    );
  };

  const actualFeatures = features
    .filter((f) => !options.find((o) => o.code === f.code))
    .map((f) => ({ value: f.code, label: f.title }));

  const adaptiveProps = {
    features,
    variants: variants.filter((v) => !v?.parentPackageId),
    variantsValidation,
    selectedVariants,
    allSelectedVariants,
    handleSelectVariant,
    handleAllSelectVariants,
    updateVariant,
    deleteVariant,
    deleteLoading,
    getCurrentFeature: (options, i) => getCurrentFeature(options, i, features),
    openVariant,
    moveVariant,
  };

  return (
    <div className="card-box">
      <div className="row">
        <div className="col-12">
          <h4 className="header-title">{t('Варианты')}</h4>
        </div>
      </div>

      {!variants.length ? (
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <Checkbox
                label={t('Использовать варианты')}
                id="useVariants"
                checked={useVariants}
                labelStyle={{ fontWeight: '400' }}
                onChange={handleVariantsUsing}
              />
            </div>
          </div>
        </div>
      ) : null}

      {useVariants ? (
        <Fragment>
          {!options.length ? (
            <div className="row">
              <div className="col-12">
                <div
                  className="alert alert-light"
                  role="alert"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <i className="fe-alert-circle mr-2" style={{ fontSize: '20px' }} />
                  <span>{t('Задайте свойства перед добавлением вариантов')}</span>
                </div>
              </div>
            </div>
          ) : null}

          {showNeedSaveAlert ? (
            <div className="row">
              <div className="col-12">
                <div className="alert alert-danger" role="alert">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className="fe-alert-circle mr-2" style={{ fontSize: '20px' }} />
                    <span>{t('Сохраните текущие изменения перед дальнейшим редактированием')}</span>
                  </div>
                  <div style={{ marginTop: '15px' }}>
                    <Button
                      type="submit"
                      text={t('Сохранить')}
                      disabled={loading}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {showOptions &&
            options.map((option, i) => (
              <div className="row option-row options" key={`option-${i}`}>
                {option.title && option.code ? (
                  <Fragment>
                    <div
                      className="col-1 sorting-block"
                      style={{ paddingLeft: '0', maxWidth: '37px' }}
                    >
                      <Button
                        classType="btn-link"
                        type="button"
                        text={
                          <i
                            className="fe-arrow-down"
                            style={{ color: '#6c757d', fontSize: '20px', display: 'block' }}
                          />
                        }
                      />
                    </div>
                    <div className="col-3 title-block" style={{ maxWidth: '145px' }}>
                      <div style={{ fontSize: '1rem', color: '#343a40' }}>{option.title}</div>
                      <div>{option.code}</div>
                    </div>
                    <div className="col-7 tags-block">
                      <TagsInput
                        appearance={getTagsInputAppearance(options, i, features, appearanceFields)}
                        value={getTagsInputValue(options, i, features)}
                        placeholder={t('Введите значение')}
                        disableLastTagDeleting={isSavedVariants}
                        disableTagAdding={
                          isSavedVariants && getTagsInputValue(options, i, features).length
                        }
                        validation={(value) => getTagsInputValidation(value, options, i, features)}
                        onChange={(optionValues, newValues) =>
                          updateOptionValues(optionValues, newValues, i)
                        }
                        onAppearanceClick={(data) => handleClickAppearance(data, option)}
                      />
                    </div>
                    <div
                      className="col-1 removing-block"
                      style={{ display: 'flex', paddingRight: '0' }}
                    >
                      {options[i].values.length < 2 ? (
                        <Button
                          style={{ marginLeft: 'auto' }}
                          classType="btn-link"
                          type="button"
                          text={
                            <i
                              className="fe-trash-2"
                              style={{ color: '#6c757d', fontSize: '20px' }}
                            />
                          }
                          onClick={() => removeOption(i)}
                        />
                      ) : null}
                    </div>
                  </Fragment>
                ) : (
                  <div className="col-12" style={{ paddingLeft: '0' }}>
                    <div className="form-group" style={{ maxWidth: '250px', margin: '10px 0' }}>
                      <Select
                        isCreatable
                        placeholder={t('Введите имя свойства')}
                        options={actualFeatures}
                        value={
                          option.title ? { value: option.code, label: option.title } : undefined
                        }
                        menuPortalTarget={document.querySelector('body')}
                        onChange={(data) => setFeatureAsOption(data, i)}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}

          {!options.length || (options.length && showOptions) ? (
            <div className="row add-option-button-row">
              <div className="col-12" style={{ paddingLeft: '0' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-light mb-2"
                  onClick={() => addOption()}
                >
                  <i className="fe-plus" /> {t('Добавить свойство')}
                </button>
              </div>
            </div>
          ) : null}

          <div className="row variants-buttons">
            <div className="col-12">
              <div className="text-lg-right">
                {options.length && !options.some((o) => !o.code) && isSavedVariants ? (
                  <button
                    type="button"
                    className="btn btn-danger waves-effect waves-light"
                    disabled={!options.length}
                    onClick={handleAddManualVariant}
                  >
                    <i className="fe-file-plus" /> {t('Новый вариант')}
                  </button>
                ) : null}
                {options.length && !showOptions ? (
                  <button
                    type="button"
                    className="btn btn-light ml-2"
                    onClick={() => setShowOptions(true)}
                  >
                    {t('Свойства')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {options.length && !options.some((o) => !o.code) ? (
            <Adaptive
              DekstopComponent={VariantsTableDesktop}
              MobileComponent={VariantsTableMobile}
              adaptiveProps={adaptiveProps}
            />
          ) : null}
        </Fragment>
      ) : null}

      {/* <pre>
                {JSON.stringify(variants,null,2)}
            </pre> */}
    </div>
  );
};

export default VariantsBlock;
